<template>
  <div id="app">
    <vue-announcer />
    <router-view />
  </div>
</template>

<script>
import axios from "axios"
import router from './router'
import Es6Promise from 'es6-promise'
Es6Promise.polyfill()

export default {
  name: 'App',
  data() {
    return {
      pageTitle: "Powered By PayGo"
    }
  },
  created() {
    // Start of App, need to evaluate the window width so we can update our components based on that
    this.$store.commit('setIsDesktop', window.innerWidth > 768);

    this.$store.dispatch('resetApp');

    // When the app first loads, we will go ahead and set the rep theme loaded flag to false
    // Once we get data for it, it will be flipped to true, this flag is used to show/hide app
    this.$store.commit('setRepThemeLoaded', false);

    // First step is to check if CSR is already logged in. If yes, we will update our store. 
    // Router will handle taking the user to home or the page they were on. If not, redirect user back to login page
    this.IsCsrLoggedIn();

    // Page Title (default)
    document.title = this.pageTitle;

    // Favicon
    this.setFavicon();
  },
  mounted() { 
    // Let's get the language file in order
    var lang = this.$store.getters.getLanguage;
    if (lang != '' && lang !== undefined && lang !== null && this.$i18n.locale !== lang) {
      this.$i18n.locale = lang;
    }
    
    this.registerAnalytics();
  },
  methods: {
    async IsCsrLoggedIn() {     
      await axios({
        method: "GET",
        url: this.$httpAxiosBaseUrl() + "Login/IsCSRLoggedIn?date=" + new Date().getTime()
      }).then(result => {
        if (result.status === 200) {
          var response = result.data;
          if (response.LoginResults === this.$LoginResults.Success) {
            this.$store.commit('setCustomerInfo', response);
            this.$store.commit('setNavigationMenuLinks', JSON.parse(response.NavigationMenuLinks));
            this.$store.commit('setAuthentication', true);

            document.title = this.$store.getters.getPageTitleFromRepThemeSettings;
          } else if (response.LoginResults === this.$LoginResults.GenericError && this.$route.meta.conditionalRoute) {
            this.$store.commit('setAuthentication', false);
            this.$store.commit('setCustomerLoginBySSOOnly', response.CustomerLoginBySSOOnly);
            this.$store.commit('setSessionEndedMessageForSSOCustomer', response.SessionEndedMessageForSSOCustomer);
            this.$router.push({ name: 'Error' });
          } else if (response.LoginResults === this.$LoginResults.SessionNotSetup) {
            this.setHeaderAndFooterImages(response);
            this.$store.commit('setCustomerLoginBySSOOnly', response.CustomerLoginBySSOOnly);
            this.$store.commit('setSessionEndedMessageForSSOCustomer', response.SessionEndedMessageForSSOCustomer);
            // Redirect only if the user is trying to access one of the conditional routes
            if (this.$route.meta.conditionalRoute) {
              this.setAuthenticationAndRedirectToLogin();
            }
          }
        } else {
          this.setAuthenticationAndRedirectToLogin();
        }

        // After we get the session information, handle the routes (beforeeach method).
        this.handleRoutes();
      }, error => {
        console.log(error);
      });
    },
    setHeaderAndFooterImages(response) {
      this.$store.commit('setHeaderImage', response.HeaderImage);
      this.$store.commit('setFooterImage', response.FooterImage);
    },
    setAuthenticationAndRedirectToLogin() {
      this.$store.commit('setAuthentication', false);
      this.$router.push({ name: 'Login' });
    },
    setFavicon() {  
      var link = document.getElementById('dynamic-favicon');
      if (link !== null) {
        var faviconUrl = this.$PageLink.LogoImage + this.$LogoType.Favicon;
        link.href = this.$formatOldCustomerPortalLinks(this.$httpAxiosBaseUrl(), faviconUrl);
      }
    },
    registerAnalytics() {
        // Google Analytics
        axios({
          method: "GET",
          url: this.$httpAxiosBaseUrl() + "Login/GetAnalyticsCode?date=" + new Date().getTime()
        }).then(result => {
          var response = result.data;
          if (response !== "") {
              // Setup the gtag script source
              var gtagSrc = "https://www.googletagmanager.com/gtag/js?id=" + response;
              
              // Now let's check if the script tag is already added to the page or not
              var gtagScriptElement = document.getElementById("gtagScript");
              
              if (gtagScriptElement !== null) {
                  gtagScriptElement.src = gtagSrc;
              } else {
                  var gtagScript = document.createElement('script');
                  gtagScript.id = "gtagScript";              
                  gtagScript.src = gtagSrc;
                  document.head.appendChild(gtagScript);
              }

              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', response);
              }                  
          }, error => {
            console.log(error);
          }
        );
    },
    handleRoutes() {      
      router.beforeEach((to, from, next) => {
        
        this.registerAnalytics();
        if (to.matched.some(record => record.meta.conditionalRoute)) {
          // Conditional Route
          if (this.$store.getters.getIsAuthenticated) {
            // Check to see whether we have permissions
            var hasPermissions = false;
            var navLinks = this.$store.getters.getNavigationMenuLinks;
            for (var i = 0; i < navLinks.length; i++) {
              // Check our links to see if the path we are tying to go to exists
              if (navLinks[i].PageLink === to.path) {
                hasPermissions = true;
                break;
              }
            }

            if (hasPermissions || to.matched.some(record => record.meta.doNotReroute)) {
              next();
            } else {
              // Redirect to the first page from our navigation links if we have one, otherwise to the login page
              next({ path: navLinks.length === 0 ? '/login' : navLinks[0].PageLink });
            }
          } else {
            next({ path: '/login' });
          }
        } else {
          // Non-conditional Route
          if (to.path.indexOf("CSR_Edit_Home.aspx") > 0) {
            window.location = this.$formatOldCustomerPortalLinks(this.$httpAxiosBaseUrl(), to.path);
          } else {
            next();
          }
        }
      });
    }
  }
}
</script>

<style>
@import url('./assets/css/bootstrap.css');
@import url('./assets/css/common.css');
@import url('./assets/css/tabs-component.css');

#app {
  font-family: 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
</style>
